import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Search, Users, X, UserPlus, MapPin, Clock } from 'lucide-react';

/**
 * Club-level Manage Players modal: search & add registered users, or create a
 * new player (guest or regular) from scratch.
 * Props:
 *   groupId            - target club id
 *   existingPlayerIds  - user_ids already in the club
 *   onClose, onAdded
 */

function extractError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || String(e)).join(', ');
  if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return err?.message || fallback;
}

const EMPTY_FORM = {
  first_name: '', last_name: '', username: '', password: '', email: '', phone: '',
  country: '', state: '', city: '',
  availability_status: 'available', available_days: [],
  available_time_start: '', available_time_end: '',
};

export default function AddPlayerModal({ groupId, existingPlayerIds, onClose, onAdded }) {
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [memberSet, setMemberSet] = useState(new Set(existingPlayerIds));
  const [form, setForm] = useState(EMPTY_FORM);
  const [isGuest, setIsGuest] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get('/players/all-registered')
      .then(r => setAllUsers(r.data))
      .catch(() => setAllUsers([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = allUsers.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.username?.toLowerCase().includes(search.toLowerCase())
  );

  const addExistingUser = async (user) => {
    setBusy(user.id);
    try {
      await api.post(`/players/${user.id}/add-to-group`, { group_id: groupId });
      toast.success(`${user.name} added to club!`);
      setMemberSet(prev => new Set([...prev, user.id]));
      onAdded();
    } catch (err) {
      toast.error(extractError(err, 'Failed to add player'));
    } finally {
      setBusy(null);
    }
  };

  const removeFromClub = async (user) => {
    setBusy(user.id);
    try {
      await api.delete(`/clubs/${groupId}/members/${user.id}`);
      toast.success(`${user.name} removed from club`);
      setMemberSet(prev => { const next = new Set(prev); next.delete(user.id); return next; });
      onAdded();
    } catch (err) {
      toast.error(extractError(err, 'Failed to remove player'));
    } finally {
      setBusy(null);
    }
  };

  const createNewPlayer = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.username || !form.password) {
      toast.error('First name, last name, username, and password are required');
      return;
    }
    if (!isGuest && !form.email) {
      toast.error('Email is required (or enable Guest mode)');
      return;
    }
    setCreating(true);
    try {
      await api.post('/players', {
        ...form,
        group_id: groupId,
        role: 'player',
        is_guest: isGuest,
        email: isGuest ? '' : form.email,
        phone: isGuest ? '' : form.phone,
      });
      toast.success(`Player "${form.first_name} ${form.last_name}" created!`);
      setForm(EMPTY_FORM);
      setIsGuest(false);
      onAdded();
      onClose();
    } catch (err) {
      toast.error(extractError(err, 'Failed to create player'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-slate-900/40 overflow-y-auto">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {showCreateForm ? 'Create New Player' : 'Manage Players'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="close-modal-btn">
            <X size={18} />
          </button>
        </div>

        {!showCreateForm ? (
          <>
            <div className="px-4 pt-4 pb-2">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                  type="text"
                  placeholder="Search registered users..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  data-testid="search-users-input"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                <span>{filtered.filter(u => memberSet.has(u.id)).length} in club · {filtered.filter(u => !memberSet.has(u.id)).length} available</span>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                  data-testid="show-create-form-btn"
                >
                  <UserPlus size={12} /> Create New
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-4" data-testid="available-users-list">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 py-3 animate-pulse">
                    <div className="w-9 h-9 bg-slate-100 rounded-full" />
                    <div className="flex-1">
                      <div className="h-3 bg-slate-100 rounded w-24 mb-1" />
                      <div className="h-2 bg-slate-100 rounded w-16" />
                    </div>
                    <div className="w-8 h-8 bg-slate-100 rounded-lg" />
                  </div>
                ))
              ) : filtered.length === 0 ? (
                <div className="py-8 text-center">
                  <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">
                    {search ? 'No users match your search' : 'No available users to add'}
                  </p>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="mt-3 text-sm text-green-600 hover:text-green-700 font-medium"
                  >
                    Create a new player instead
                  </button>
                </div>
              ) : (
                filtered.map((user, i) => {
                  const isInClub = memberSet.has(user.id);
                  return (
                    <div
                      key={user.id}
                      className={`flex items-center gap-3 py-2.5 ${i < filtered.length - 1 ? 'border-b border-slate-100' : ''}`}
                      data-testid={`player-row-${user.id}`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
                        isInClub ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {(user.name || user.username)?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{user.name || user.username}</p>
                        <p className="text-xs text-slate-400 truncate">
                          @{user.username}
                          {user.doubles_rating ? <span className="font-mono ml-1">· {user.doubles_rating}</span> : ''}
                          {isInClub && <span className="text-green-600 font-medium"> · In club</span>}
                        </p>
                      </div>
                      {isInClub ? (
                        <button
                          onClick={() => removeFromClub(user)}
                          disabled={busy === user.id}
                          className="w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 flex items-center justify-center transition-all disabled:opacity-50 text-lg font-bold"
                          title={`Remove ${user.name} from club`}
                          data-testid={`remove-user-btn-${user.id}`}
                        >
                          {busy === user.id ? '...' : '−'}
                        </button>
                      ) : (
                        <button
                          onClick={() => addExistingUser(user)}
                          disabled={busy === user.id}
                          className="w-8 h-8 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 border border-green-200 flex items-center justify-center transition-all disabled:opacity-50 text-lg font-bold"
                          title={`Add ${user.name} to club`}
                          data-testid={`add-user-btn-${user.id}`}
                        >
                          {busy === user.id ? '...' : '+'}
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <div className="p-4 overflow-y-auto">
            <form onSubmit={createNewPlayer} className="space-y-3" data-testid="create-player-form">
              <label className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isGuest}
                  onChange={e => setIsGuest(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                  data-testid="new-player-is-guest"
                />
                <span className="text-xs font-medium text-amber-800">
                  Guest player (no email/phone — for kids or casual visitors)
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="e.g. John"
                    className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="new-player-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="e.g. Smith"
                    className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="new-player-last-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Username *</label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                    placeholder="Login username"
                    className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="new-player-username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Initial password"
                    className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="new-player-password"
                  />
                </div>
              </div>
              {!isGuest && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="you@email.com"
                      className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      data-testid="new-player-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Phone <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="+1 234 567 8900"
                      className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      data-testid="new-player-phone"
                    />
                  </div>
                </div>
              )}
              <div className="pt-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1 mb-2"><MapPin size={11} /> Location</p>
                <div className="grid grid-cols-3 gap-2">
                  <input type="text" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                    placeholder="Country" className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="new-player-country" />
                  <input type="text" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                    placeholder="State" className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="new-player-state" />
                  <input type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="City" className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="new-player-city" />
                </div>
              </div>
              <div className="pt-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1 mb-2"><Clock size={11} /> Availability</p>
                <select value={form.availability_status} onChange={e => setForm(f => ({ ...f, availability_status: e.target.value }))}
                  className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 mb-2" data-testid="new-player-availability-status">
                  <option value="available">Available</option>
                  <option value="busy">Busy</option>
                  <option value="away">Away</option>
                </select>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {['mon','tue','wed','thu','fri','sat','sun'].map(d => (
                    <button key={d} type="button"
                      onClick={() => setForm(f => ({ ...f, available_days: f.available_days.includes(d) ? f.available_days.filter(x => x !== d) : [...f.available_days, d] }))}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${form.available_days.includes(d) ? 'bg-green-100 border-green-300 text-green-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                      data-testid={`new-player-day-${d}`}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="time" value={form.available_time_start} onChange={e => setForm(f => ({ ...f, available_time_start: e.target.value }))}
                    className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="new-player-time-start" />
                  <input type="time" value={form.available_time_end} onChange={e => setForm(f => ({ ...f, available_time_end: e.target.value }))}
                    className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="new-player-time-end" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-all"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 h-10 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-lg transition-all"
                  data-testid="submit-create-player"
                >
                  {creating ? 'Creating...' : 'Create Player'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
